/**
 * `useDocumentDetails` — the A-14 read half: provenance + user notes for a
 * Generated-tier document, in ONE parent-keyed call.
 *
 * Contract: `case.document_details_read` per
 * `ai-docs/a14-kernel-half-runtime-miniplan.md` §6 (typed EXACTLY as named in
 * `types/gateway.ts`). ⚖ The R6 pin lives here on the consumer side too: the
 * seam delivers `notes` + `notesAuthoredBy` INLINE with provenance, so this
 * hook — and therefore the pane — never names a sidecar and never computes a
 * sibling path. Constructing the notes-sidecar filename anywhere in the SPA
 * silently degrades ruling (b) to (a); the SPA-wide T9b grep pin
 * (`DocumentDetailsPane.test.tsx`) reddens on the literal — including in a
 * comment, which is exactly how ITS OWN first draft tripped it.
 *
 * Contract rules honored by construction:
 * - `provenance: null` (not recorded — a real state) vs `{entries: []}`
 *   (recorded, empty) reach the caller UNCHANGED — no `?? []` collapse.
 * - A FAILED read is `isError`, never a fabricated "no details" (the seam
 *   throws instead of returning the absence shape; this hook adds no catch
 *   that would undo that).
 */

import { useQuery } from "@tanstack/react-query";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useActiveContextStore, useEffectiveScope } from "@/stores/useActiveContextStore";
import type { CaseDocumentDetailsResult } from "@/types/gateway";

export interface UseDocumentDetailsResult {
  details: CaseDocumentDetailsResult | null;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useDocumentDetails(parentPath: string | null): UseDocumentDetailsResult {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const selection = useActiveContextStore((s) => s.selection);
  const scope = useEffectiveScope();
  const caseId = selection?.caseId ?? null;

  const query = useQuery({
    // caseId in the key: a case→case navigation with the same relative path
    // (two cases both holding `analyses/analise.md`) must never serve the
    // other case's notes from cache.
    queryKey: ["document-details", caseId, parentPath],
    queryFn: async (): Promise<CaseDocumentDetailsResult> => {
      if (!rpc) throw new Error("Gateway not connected");
      if (!caseId || !parentPath) throw new Error("No case scope or path");
      return rpc.caseDocumentDetailsRead({ caseId, parentPath });
    },
    enabled:
      Boolean(rpc) &&
      connectionState === "connected" &&
      scope !== "org" &&
      Boolean(caseId) &&
      Boolean(parentPath),
    retry: 1,
  });

  return {
    details: query.data ?? null,
    isPending: query.isPending,
    isError: query.isError,
    error: (query.error as Error) ?? null,
    refetch: () => void query.refetch(),
  };
}
